/**
 * YearEndPage
 *
 * 스레드 연말정산 페이지 (2025)
 * 듀얼 모드 지원:
 * - /year-end: 내 페이지 (로그인 필수)
 * - /year-end/:id: 공유 페이지 (공개 여부에 따라 접근)
 *
 * - 1회 발행 정책: 첫 방문 시 계산 → 발행 후 캐시 사용
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../components/Container/PageContainer';
import { useYearEndData } from '../hooks/useYearEndData';
import { useUser } from '../contexts/UserContext';

// 기본 상태 컴포넌트
import {
  YearEndSkeleton,
  OrbitalLoadingView,
  EmptyYearEnd,
  YearEndLoginPrompt,
  YearEndFooter,
  WordConfetti,
} from '../components/YearEnd';

// 섹션 템플릿
import {
  YearEndHeroSection,
  YearOfTheThreadsSection,
  NotableThreadsSection,
  YearActivitySummarySection,
  MonthlyReportSection,
  FriendsOfTheYearSection,
} from '../template/YearEnd';

const YearEndPage = () => {
  const { t } = useTranslation();
  const { id: sharedId } = useParams(); // /year-end/:id에서 추출
  const { isLogin, profile } = useUser();
  const year = 2025;

  // Confetti 상태 (1회만 실행)
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiPlayed, setConfettiPlayed] = useState(false);

  // 듀얼 모드 지원: sharedId가 있으면 공유 페이지 모드
  const hookResult = useYearEndData(year, sharedId);
  const {
    yearEndData = null,
    yearEndId = null,
    status = 'loading',
    isPublished = false,
    isMyPage = true,
    isFriendsLoading = false,
    loadingState = null,
    activitySummary = null,
  } = hookResult ?? {};

  // monthlyReport에서 스레드 제목 추출 (Hero 배경용)
  const threadTitles = useMemo(() => {
    if (!yearEndData?.monthlyReport) return [];
    const titles = [];
    Object.values(yearEndData.monthlyReport).forEach(month => {
      if (month?.topThreads) {
        month.topThreads.forEach(thread => {
          if (thread?.text) {
            // 첫 문장만 추출 (50자 제한)
            const firstSentence = thread.text.split(/[.!?。]/)[0]?.trim();
            if (firstSentence && firstSentence.length > 5) {
              titles.push(firstSentence.slice(0, 50));
            }
          }
        });
      }
    });
    return titles;
  }, [yearEndData?.monthlyReport]);

  // 데이터 로드 완료 시 Confetti 트리거 (1회만)
  useEffect(() => {
    if (status === 'ready' && yearEndData && !confettiPlayed) {
      // 약간의 딜레이 후 실행 (페이지 렌더링 완료 대기)
      const timer = setTimeout(() => {
        setShowConfetti(true);
        setConfettiPlayed(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [status, yearEndData, confettiPlayed]);

  // 비로그인 + 내 페이지 모드 → 로그인 유도
  if (!isLogin && isMyPage) {
    return (
      <PageContainer>
        <YearEndLoginPrompt year={year} />
      </PageContainer>
    );
  }

  // 로딩 중 - Orbital 인터랙티브 로딩 화면
  if (status === 'loading') {
    return (
      <PageContainer>
        <OrbitalLoadingView
          loadingState={loadingState}
          year={year}
        />
      </PageContainer>
    );
  }

  // 인증 실패
  if (status === 'unauthorized') {
    return (
      <PageContainer>
        <YearEndLoginPrompt year={year} />
      </PageContainer>
    );
  }

  // 페이지 없음 (공유 모드)
  if (status === 'not_found') {
    return (
      <PageContainer>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('yearEnd.notFound.title')}
          </Typography>
          <Typography color="text.secondary">
            {t('yearEnd.notFound.description')}
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  // 비공개 (공유 모드)
  if (status === 'private') {
    return (
      <PageContainer>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('yearEnd.private.title')}
          </Typography>
          <Typography color="text.secondary">
            {t('yearEnd.private.description', { username: yearEndData?.username || '' })}
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  // 데이터 없음
  if (status === 'empty') {
    return (
      <PageContainer>
        <EmptyYearEnd year={year} />
      </PageContainer>
    );
  }

  // 에러 발생
  if (status === 'error') {
    return (
      <PageContainer>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('yearEnd.error.title', '오류가 발생했습니다')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t('yearEnd.error.description', '연말정산 데이터를 불러오는 중 문제가 발생했습니다.')}
          </Typography>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            {t('yearEnd.error.retry', '다시 시도')}
          </Button>
        </Box>
      </PageContainer>
    );
  }

  // 데이터 아직 준비 안됨 (status는 ready인데 데이터가 null인 경우)
  if (!yearEndData) {
    return (
      <PageContainer>
        <OrbitalLoadingView
          loadingState={loadingState}
          year={year}
        />
      </PageContainer>
    );
  }

  // 공유 모드에서 사용할 프로필 정보
  const displayProfile = isMyPage ? profile : {
    id: yearEndData?.userId,
    username: yearEndData?.username,
    threads_profile_picture_url: yearEndData?.profileImage,
  };

  // 데이터 준비 완료
  return (
    <PageContainer>
      {/* Word Confetti 효과 (1회만) */}
      {showConfetti && (
        <WordConfetti
          yearEndData={yearEndData}
          onComplete={() => setShowConfetti(false)}
        />
      )}

      {/* Hero Section */}
      <YearEndHeroSection
        year={year}
        profile={displayProfile}
        stats={yearEndData?.stats}
        threadTitles={threadTitles}
        isSharedView={!isMyPage}
      />

      {/* Year of the Threads (Top 3) */}
      <YearOfTheThreadsSection
        yearOfTheThreads={yearEndData?.yearOfTheThreads}
        ownerProfile={displayProfile}
      />

      {/* Notable Threads */}
      <NotableThreadsSection
        notableThreads={yearEndData?.notableThreads}
        ownerProfile={displayProfile}
      />

      {/* Year Activity Summary - 내 페이지에서만 표시 (공유 페이지는 데이터 없음) */}
      {isMyPage && (
        <YearActivitySummarySection
          activitySummary={activitySummary}
          isLoading={status === 'loading'}
        />
      )}

      {/* Friends of the Year */}
      <FriendsOfTheYearSection
        friendsOfTheYear={yearEndData?.friendsOfTheYear}
        isFriendsLoading={isFriendsLoading}
        ownerProfile={displayProfile}
      />

      {/* Monthly Report */}
      <MonthlyReportSection
        monthlyReport={yearEndData?.monthlyReport}
        mostActiveMonth={yearEndData?.stats?.mostActiveMonth}
        ownerProfile={displayProfile}
      />

      {/* Footer with Share Controls */}
      <YearEndFooter
        isMyPage={isMyPage}
        yearEndId={yearEndId}
        viewCount={yearEndData?.viewCount}
      />
    </PageContainer>
  );
};

export default YearEndPage;
